import React from "react";
import { useFragment } from "react-relay";

import { faClose } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import { graphql } from "relay-runtime";

import useMedia from "~/lib/hooks/useMedia";
import { useNewRequestAccessFeatureFlagOn } from "~/meetings/featureFlag";
import Avatar from "~/ui/avatar/Avatar";
import AvatarList from "~/ui/avatar/AvatarList";
import Button from "~/ui/button/Button";
import IconButton from "~/ui/button/IconButton";
import Tooltip from "~/ui/Tooltip";

import type {
	RequestAccessTooltip_note$data,
	RequestAccessTooltip_note$key,
} from "~/__generated__/RequestAccessTooltip_note.graphql";

import styles from "./RequestAccessTooltip.module.css";

const ACCESS_REQUEST_STORAGE_KEY = "fellow_access_request_timestamps";

// Hook to manage note-specific access request timestamps
function useAccessRequestTimestamps() {
	const [timestamps, setTimestamps] = React.useState<Record<string, string>>({});

	// Load timestamps from localStorage on mount
	React.useEffect(() => {
		try {
			const stored = localStorage.getItem(ACCESS_REQUEST_STORAGE_KEY);
			if (stored) {
				setTimestamps(JSON.parse(stored));
			}
		} catch (error) {
			console.warn("Failed to load access request timestamps from localStorage:", error);
		}
	}, []);

	// Save timestamps to localStorage whenever they change
	const saveTimestamps = React.useCallback((newTimestamps: Record<string, string>) => {
		try {
			localStorage.setItem(ACCESS_REQUEST_STORAGE_KEY, JSON.stringify(newTimestamps));
			setTimestamps(newTimestamps);
		} catch (error) {
			console.warn("Failed to save access request timestamps to localStorage:", error);
		}
	}, []);

	// Update the last seen timestamp for a specific note
	const updateLastAccessRequestTimestamp = React.useCallback(
		(noteId: string, timestamp: string) => {
			saveTimestamps({
				...timestamps,
				[noteId]: timestamp,
			});
		},
		[timestamps, saveTimestamps],
	);

	// Get the last seen timestamp for a specific note
	const getLastAccessRequestTimestamp = React.useCallback(
		(noteId: string): string | null => {
			return timestamps[noteId] || null;
		},
		[timestamps],
	);

	return {
		getLastAccessRequestTimestamp,
		updateLastAccessRequestTimestamp,
	};
}

const noteFragment = graphql`
	fragment RequestAccessTooltip_note on Note {
		id
		pendingAccessRequests {
			id
			createdAt
			requestor {
				id
				fullName
				email
				image
				isExternal
			}
		}
	}
`;

function RequestAccessTooltipContent({
	pendingAccessRequests,
	openRequestsAccessModal,
	onRequestAccessTooltipDismiss,
}: {
	pendingAccessRequests: RequestAccessTooltip_note$data["pendingAccessRequests"];
	openRequestsAccessModal: () => void;
	onRequestAccessTooltipDismiss: () => void;
}) {
	const users = pendingAccessRequests.map(request => request.requestor);
	const isSingleUser = users.length === 1;

	if (users.length === 0) {
		return null;
	}

	return (
		<div className={styles.tooltipRow}>
			{isSingleUser ? (
				<Avatar user={users[0]} size="large" />
			) : (
				<AvatarList hideDetailsTooltip users={users} size="large" maxLength={3} />
			)}

			<div className={styles.tooltipText}>
				{isSingleUser ? (
					<>
						<span className={styles.tooltipName}>{users[0].fullName}</span> has requested access
					</>
				) : (
					<>
						<span className={styles.tooltipName}>{users.length} people</span> have requested access
					</>
				)}
			</div>

			<div className={styles.tooltipButtons}>
				<Button variant="primary" size="slim" onClick={openRequestsAccessModal}>
					Review
				</Button>

				<IconButton variant="tertiary" onClick={onRequestAccessTooltipDismiss} icon={faClose} />
			</div>
		</div>
	);
}

export function RequestAccessTooltip({
	note: noteKey,
	openRequestsAccessModal,
	children,
}: {
	note: RequestAccessTooltip_note$key;
	openRequestsAccessModal: () => void;
	children: React.ReactNode;
}) {
	const note = useFragment(noteFragment, noteKey);
	const { getLastAccessRequestTimestamp, updateLastAccessRequestTimestamp } = useAccessRequestTimestamps();

	const navIsInMobileMode = useMedia("(max-width: 735px)");
	const hasPendingAccessRequests = React.useMemo(
		() => note.pendingAccessRequests.length > 0,
		[note.pendingAccessRequests],
	);

	// Check if there are new access requests since last dismissal
	const hasNewAccessRequests = React.useMemo(() => {
		if (!hasPendingAccessRequests) return false;

		const lastAccessRequestTimestamp = getLastAccessRequestTimestamp(note.id);
		if (!lastAccessRequestTimestamp) {
			// If no previous timestamp, all requests are new
			return true;
		}

		// Check if any request is newer than the last seen timestamp
		return note.pendingAccessRequests.some(
			request => new Date(request.createdAt) > new Date(lastAccessRequestTimestamp),
		);
	}, [note, hasPendingAccessRequests, getLastAccessRequestTimestamp]);

	const showRequestAccessTooltip = React.useMemo(
		() => !navIsInMobileMode && hasPendingAccessRequests && hasNewAccessRequests,
		[navIsInMobileMode, hasPendingAccessRequests, hasNewAccessRequests],
	);

	const onRequestAccessTooltipDismiss = React.useCallback(() => {
		// Find the most recent access request timestamp
		const mostRecentTimestamp = note.pendingAccessRequests.reduce(
			(latest, request) => {
				const requestTime = new Date(request.createdAt);
				const latestTime = latest ? new Date(latest) : new Date(0);
				return requestTime > latestTime ? request.createdAt : latest;
			},
			null as string | null,
		);

		// Update the last access request timestamp for this note
		if (mostRecentTimestamp) {
			updateLastAccessRequestTimestamp(note.id, mostRecentTimestamp);
		}
	}, [note, updateLastAccessRequestTimestamp]);

	return (
		<Tooltip
			placement="bottom"
			visible={showRequestAccessTooltip}
			interactive
			className={cx(styles.tooltip, "invert-theme")}
			content={
				<RequestAccessTooltipContent
					pendingAccessRequests={note.pendingAccessRequests}
					openRequestsAccessModal={openRequestsAccessModal}
					onRequestAccessTooltipDismiss={onRequestAccessTooltipDismiss}
				/>
			}
			maxWidth={450}
		>
			<div className={styles.wrapper}>
				{/* Only one of these share buttons is visible at a time, see CSS. */}
				{children}

				{hasPendingAccessRequests && <div className={styles.attentionDot} />}
			</div>
		</Tooltip>
	);
}

// Wrapped component that checks the feature flag
function RequestAccessTooltipWithFeatureFlag({
	note,
	openRequestsAccessModal,
	children,
}: {
	note: RequestAccessTooltip_note$key;
	openRequestsAccessModal: () => void;
	children: React.ReactNode;
}) {
	const isNewRequestAccessEnabled = useNewRequestAccessFeatureFlagOn();

	if (!isNewRequestAccessEnabled) {
		return <>{children}</>;
	}

	return (
		<RequestAccessTooltip note={note} openRequestsAccessModal={openRequestsAccessModal}>
			{children}
		</RequestAccessTooltip>
	);
}

export default RequestAccessTooltipWithFeatureFlag;
